import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { AxiosError } from "axios";
import { IEventSummary, IUser } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import { apiService } from "src/services/api.service";
import { helperService} from "../../services/helper.service";

import { useTabContent } from "src/components/TabContent/TabContent";
import { toast } from "src/components/Toast/ToastManager";
import RegistrantsTab from "./RegistrantsTab/RegistrantsTab";
import DiscountsTab from "./DiscountsTab/DiscountsTab";
import ResultsTab from "./ResultsTab/ResultsTab";
import MoveUpsTab from "./MoveUpsTab/MoveUpsTab";
import TransactionsTab from "./TransactionsTab/TransactionsTab";
import RefundsTab from "./RefundsTab/RefundsTab";
import WaitlistTab from "./WaitlistTab/WaitlistTab";
import AddOnsTab from "./AddOnsTab/AddOnsTab";
import GateStewardsTab from "./GateStewardsTab/GateStewardsTab";
import IncentivesTab from "./IncentivesTab/IncentivesTab";
import EventManageSidebar from "./EventManageSidebar";
import { getManageEventSectionIndex, MANAGE_EVENT_ENTRIES_TAB_INDEX, MANAGE_EVENT_SECTIONS } from "./manage-event-sections";

import styles from "./EventManage.module.scss";

enum eventErrors {
  NOT_AUTHORIZED,
  NOT_FOUND,
  NO_METRICS
}

interface IEventMetrics {
  dogs: number,
  entries: number,
  revenue: number
}

const EventManage: React.FC = () => {
  const [loaded, setLoaded] = useState<boolean>(false);

  const [event, setEvent] = useState<IEventSummary>();
  const [metrics, setMetrics] = useState<IEventMetrics>();
  const [onlineRevenue, setOnlineRevenue] = useState<number>(0);
  const [offlineRevenue, setOfflineRevenue] = useState<number>(0);
  const [refundedRevenue, setRefundedRevenue] = useState<number>(0);
  const [refundedGrossRevenue, setRefundedGrossRevenue] = useState<number>(0);
  const [unpaidCount, setUnpaidCount] = useState<number>(0);
  const [requestedRefundCount, setRequestedRefundCount] = useState<number>(0);

  const navigate = useNavigate();
  const user:IUser = helperService.getUser();
  let { eid } = useParams();
  const [searchParams] = useSearchParams();
  const { activeTab, setTab } = useTabContent();
  const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);
  const activeSection = MANAGE_EVENT_SECTIONS.find((section) => section.tabIndex === activeTab);
  const pageLabel = activeSection?.label ?? "Manage Event";
  const showMetrics = activeSection?.id === "registrations";

  // Helper function to construct breadcrumb URL with admin context
  const getBreadcrumbUrl = useCallback(() => {
    const adminParam = searchParams.get('admin');
    const baseUrl = `/e/${eid}`;
    return adminParam ? `${baseUrl}?admin=${adminParam}` : baseUrl;
  }, [searchParams, eid]);

  const [currencyFormatter, setCurrentFormatter] = useState<any>(new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }));

  const hasAdminAccess = useCallback(async (clubId: string, userId: string, createdBy: string) => {
    // First check if user is the club owner (original logic)
    if (createdBy === userId) {
      return true;
    }

    // For dog_owner accounts, check if they have admin capacity for this club
    if (user.user_type === Constants.user_type.owner) {
      try {
        const capacitiesResponse = await apiService.getUserCapacities();
        const hasAdminCapacity = capacitiesResponse.capacities.some(capacity => 
          capacity.club_id === clubId && capacity.capacities.includes(Constants.capacity_type.admin)
        );
        return hasAdminCapacity;
      } catch (error) {
        console.error('EventManage: Failed to check user capacities:', error);
        // If we're coming from an admin dashboard context (indicated by the referrer),
        // and we get a 401, it's likely a temporary auth issue - allow access
        const referrer = document.referrer;
        const isFromAdminDashboard = referrer.includes('/admin/clubs/');
        if (isFromAdminDashboard) {
          return true;
        }
        return false;
      }
    }

    return false;
  }, [user.user_type]);

  const getEvent = useCallback((eid: string) => {
    return new Promise(async (resolve) => {
      try {
        const response = await apiService.getEventSummaryForHandle(eid);
        if (response?.club?.created_by) {
          const hasAccess = await hasAdminAccess(response.club._id, user._id, response.club.created_by);
          if (!hasAccess) {
            resolve(eventErrors.NOT_AUTHORIZED);
          } else {
            resolve(response);
          }
        }
      } catch (e: any) {
        resolve(eventErrors.NOT_FOUND);
      }
    });
  }, [user._id, hasAdminAccess]);

  const getEventMetrics = (eventId: string) => {
    return new Promise((resolve) => {
      apiService.getEventMetrics(eventId).then((response) => {
        resolve(response);
      }).catch((e: AxiosError) => {
        resolve(eventErrors.NO_METRICS);
      });
    });
  }

  const getEventRevenueBuckets = useCallback((eventId: string, currency: string) => {
    apiService.getEventTransactions(eventId, {
      query: {
        $and: [{
          attribute_name: "status",
          attribute_value: {
            operator: "$eq",
            value: "success"
          }
        }]
      }
    }).then((transactions) => {
      let online = 0;
      let offline = 0;
      let refundedGross = 0;
      let refundedClubCost = 0;
      for (const tx of transactions) {
        const multiplier = tx.amounts?.amount_multiplier || 1;
        const processedAmount = tx.amounts?.processed_amount || 0;
        const refundedAmount = tx.amounts?.refunded_amount || 0;
        // EagerDog fee Stripe returned to the club alongside the refund: the
        // club's real cost of the refund is refunded_amount minus this.
        const refundedAppFee = Math.min(refundedAmount, tx.amounts?.refunded_application_fee || 0);
        
        if (tx.amounts?.payment_method_type === 'card' || tx.amounts?.payment_method_type === 'bank_account') {
          // Online (card or bank account) - processed_amount is already correct
          const amt = Math.round((multiplier * processedAmount) * 100) / 100;
          online += amt;
        } else {
          // Offline (cash/cheque/manual) - processed_amount is already net of discount (set by backend)
          const amt = Math.round((multiplier * processedAmount) * 100) / 100;
          offline += amt;
        }
        // Accumulate refunds across all payment methods
        refundedGross += Math.round((multiplier * refundedAmount) * 100) / 100;
        refundedClubCost += Math.round((multiplier * (refundedAmount - refundedAppFee)) * 100) / 100;
      }
      setOnlineRevenue(online);
      setOfflineRevenue(offline);
      // Total Revenue subtracts the club's NET refund cost so it matches the
      // club's Stripe balance (same as the Excel export's Total Club Revenue).
      setRefundedRevenue(refundedClubCost);
      setRefundedGrossRevenue(refundedGross);
    });
  }, []);

  useEffect(() => {
    if (!loaded && eid) {
      const queryParameters = new URLSearchParams(window.location.search);
      const paramShow = queryParameters.get("show");
      const paramTab = queryParameters.get("tab");

      if (paramShow) {
        setTab(MANAGE_EVENT_ENTRIES_TAB_INDEX);
      } else if (paramTab) {
        const tabIndex = getManageEventSectionIndex(paramTab);
        if (tabIndex !== undefined) {
          setTab(tabIndex);
        }
      }

      const showError = (msg: string, url?: string) => {
        toast.show({
          title: "Event Manage",
          content: msg,
          duration: 10000,
          type: "fail"
        });

        if (url) {
          navigate(url);
        }
      }

      const loadEventData = async () => {
        if (eid) {
          const event:any = await getEvent(eid);

          if (event._id) {
            setEvent(event);

            setCurrentFormatter(new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: event.currency || "USD",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }));

            const metrics:any = await getEventMetrics(event._id);

            if (metrics.dogs !== undefined && metrics.entries !== undefined && metrics.revenue !== undefined) {
              setMetrics(metrics);
              getEventRevenueBuckets(event._id, event.currency || "USD");
            } else {
              showError("Error getting metrics");
            }

            // Load unpaid count for Transactions tab badge
            try {
              const unpaidResult = await apiService.getUnpaidTransactionCount(event._id);
              setUnpaidCount(unpaidResult.count);
            } catch (_e) {
              // non-critical — badge stays at 0
            }

            // Load requested refund count for Refund Requests tab badge
            try {
              const refundResult = await apiService.getRequestedRefundCount(event._id);
              setRequestedRefundCount(refundResult.count);
            } catch (_e) {
              // non-critical — badge stays at 0
            }
          } else {
            if (event === eventErrors.NOT_FOUND) {
              showError("Event not found", "/");
            } else {
              showError("Only club owners can manage an event", "/e/" + eid);
            }
          }

          setLoaded(true);
        }
      }

      loadEventData();
    }
  }, [loaded, eid, getEvent, navigate, setTab, getEventRevenueBuckets]);

  return (
    <div className={styles.EventManage}>
      {event && <Helmet>
        <title>{event?.title} - {pageLabel}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>}
      <EventManageSidebar
        eventTitle={event?.title}
        clubName={event?.club_name || event?.club?.club_name}
        clubLogoUrl={event?.club?.logo_url}
        clubLocation={[event?.address?.city, event?.address?.state, event?.address?.country].filter(Boolean).join(", ")}
        eventUrl={getBreadcrumbUrl()}
        activeTab={activeTab}
        badges={{
          transactions: unpaidCount,
          "refund-requests": requestedRefundCount,
        }}
        onSelect={setTab}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />
      <div className={styles.main}>
        <div className={styles.inner}>
          <div className={styles.pageHeader}>
            <button
              type="button"
              className={styles.mobileMenu}
              onClick={() => setMobileNavOpen(true)}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M4 6h16M4 12h16M4 18h10" />
              </svg>
              Event menu
            </button>
          </div>
          <h1>{pageLabel}</h1>
          {metrics && showMetrics && <div className={styles.metrics}>
            <div className={styles.metricItem}>
              <div className={styles.num}>{metrics.dogs}</div>
              <div className={styles.label}>Dogs Registered</div>
            </div>
            <div className={styles.metricItem}>
              <div className={styles.num}>{metrics.entries}</div>
              <div className={styles.label}>Entries</div>
            </div>
            <div className={styles.metricItem}>
              <div className={styles.num}>
                {currencyFormatter.format(onlineRevenue + offlineRevenue - refundedRevenue)}
              </div>
              <div className={styles.label}>Total Revenue</div>
              <div
                className={styles.revenueBreakdown}
                title={
                  refundedGrossRevenue > refundedRevenue
                    ? `Exhibitors were refunded ${currencyFormatter.format(refundedGrossRevenue)} in total; ${currencyFormatter.format(refundedGrossRevenue - refundedRevenue)} of that was the Eager Dog fee returned to you by Stripe, so your net cost is ${currencyFormatter.format(refundedRevenue)}.`
                    : undefined
                }
              >
                Online {currencyFormatter.format(onlineRevenue)} Offline {currencyFormatter.format(offlineRevenue)} Refunded -{currencyFormatter.format(refundedRevenue)}
              </div>
            </div>
          </div>}
          {event && <div className={styles.sections}>
            <div className={activeTab === 0 ? `${styles.section} ${styles.sectionActive}` : styles.section}>
              <RegistrantsTab currentTab={activeTab} tabIndex={0} event={event} />
            </div>
            <div className={activeTab === 1 ? `${styles.section} ${styles.sectionActive}` : styles.section}>
              <ResultsTab currentTab={activeTab} tabIndex={1} event={event} />
            </div>
            <div className={activeTab === 2 ? `${styles.section} ${styles.sectionActive}` : styles.section}>
              <MoveUpsTab currentTab={activeTab} tabIndex={2} event={event} />
            </div>
            <div className={activeTab === 3 ? `${styles.section} ${styles.sectionActive}` : styles.section}>
              <TransactionsTab currentTab={activeTab} tabIndex={3} event={event} />
            </div>
            <div className={activeTab === 4 ? `${styles.section} ${styles.sectionActive}` : styles.section}>
              <RefundsTab currentTab={activeTab} tabIndex={4} event={event} />
            </div>
            <div className={activeTab === 5 ? `${styles.section} ${styles.sectionActive}` : styles.section}>
              <WaitlistTab currentTab={activeTab} tabIndex={5} event={event} />
            </div>
            <div className={activeTab === 6 ? `${styles.section} ${styles.sectionActive}` : styles.section}>
              <AddOnsTab currentTab={activeTab} tabIndex={6} event={event} />
            </div>
            <div className={activeTab === 7 ? `${styles.section} ${styles.sectionActive}` : styles.section}>
              <DiscountsTab currentTab={activeTab} tabIndex={7} event={event} />
            </div>
            <div className={activeTab === 8 ? `${styles.section} ${styles.sectionActive}` : styles.section}>
              <IncentivesTab currentTab={activeTab} tabIndex={8} event={event} />
            </div>
            <div className={activeTab === 9 ? `${styles.section} ${styles.sectionActive}` : styles.section}>
              <GateStewardsTab currentTab={activeTab} tabIndex={9} event={event} />
            </div>
          </div>}
        </div>
      </div>
    </div>
  );
};

export default EventManage;